import Vue from 'vue'
import Axios from 'axios'
import Vuex from 'vuex'
import VuexPersistence from 'vuex-persist'
import VueJwtDecode from 'vue-jwt-decode'
import createMutationsSharer from 'vuex-shared-mutations'

Vue.use(Vuex)

const vuexCookie = new VuexPersistence({
  storage: window.localStorage,
  filter: (mutation) => (
    mutation.type === 'mutateAuth' ||
    mutation.type === 'clearAuth' ||
    mutation.type === 'clearAll' ||
    mutation.type === 'mutateModalConfirm' ||
    mutation.type === 'mutateNewTab' ||
    mutation.type === 'mutateUserOffices' ||
    mutation.type === 'mutateCurrentOffice' ||
    mutation.type === 'mutatePlan' ||
    mutation.type === 'mutatePlanShowModal' ||
    mutation.type === 'mutatePlanRemainingDays' ||
    mutation.type === 'mutatePlanIsUpgrade'
  )
})

const sharedMutations = createMutationsSharer({
  predicate: (mutation, state) => {
    const predicate = [
      'mutateActiveAppointments',
      'mutateCurrentOffice',
      'mutateUserOffices',
      'mutateNewTab',
      'mutateAuth',
      'clearAuth'
    ]
    return predicate.indexOf(mutation.type) >= 0
  }
})

const store = new Vuex.Store({
  plugins: [vuexCookie.plugin, sharedMutations],
  state: {
    $_mapsApi: null,
    progress: false,
    showSidebar: false,
    cancelToken: Axios.CancelToken.source(),
    autocomplete: {},
    activeAppointments: [],
    newCertifierCredentialRequired: false,
    modalAlert: {
      title: null,
      body: null,
      active: false,
      size: 'sm',
      callback: null
    },
    modalConfirm: {
      title: null,
      body: null,
      footer: null,
      active: false,
      size: 'sm',
      confirmText: null,
      beforeClose: null,
      callback: null,
      cancelText: null
    },
    $_session: {
      $_token: null,
      $_refreshToken: null,
      $_user: null,
      $_timeStamp: null,
      $_roles: [],
      $_accessRoles: [],
      $_currentOffice: {},
      $_offices: [],
      $_appIntegration: null,
      $_healthPlaceIntegration: null,
      $_profile: {
        id: null,
        name: null,
        email: null,
        displayName: null,
        picture: null,
        doctorList: []
      }
    },
    registration: {
      appointmentInterval: null
    },
    callStack: 0,
    tour: {
      progress: 0,
      step: null
    },
    newTab: null,
    actionModal: false,
    telemedicine: {
      isBroadcasting: false,
      isActive: false
    },
    appointmentStart: false,
    isSidebarToggled: false,
    viewport: null,
    windowWidth: null,

    plan: {
      isPro: false,
      isPremium: false,
      isTrial: false,
      isUpgrade: false,
      showModal: false,
      isReminderModal: false,
      remainingDays: 0,
      proId: null,
      premiumId: null,
      premiumPrice: 0,
      proPrice: 0,
      marketplacePrice: 0,
      isCTA: false
    }
  },
  mutations: {
    mutateUserOffices (state, data) {
      state.$_session.$_offices = data
    },
    mutateCurrentOffice (state, data) {
      state.$_session.$_currentOffice = state.$_session.$_offices
        .find(office => office.id === data)
    },
    removeCurrentOffice (state) {
      state.$_session.$_currentOffice = {}
    },
    mutateUserProfile (state, data) {
      state.$_session.$_profile.id = data.id
      state.$_session.$_profile.name = data.name
      state.$_session.$_profile.displayName = data.displayName
      state.$_session.$_profile.picture = data.picture
      state.$_session.$_profile.doctorList = data.doctorList
      state.$_session.$_profile.email = data.email
    },
    mutateUserProfileField (state, data) {
      for (let field in data) {
        if (state.$_session.$_profile[field]) {
          state.$_session.$_profile[field] = data[field]
        }
      }
    },
    mutateAuth (state, data) {
      const decodedData = VueJwtDecode.decode(data.token)
      state.$_mapsApi = process.env.GMAPS_API_KEY
      state.$_session.$_token = data.token
      state.$_session.$_refreshToken = data.refresh_token
      state.$_session.$_user = decodedData.username
      state.$_session.$_timeStamp = new Date(decodedData.iat * 1000)
      state.$_session.$_roles = decodedData.roles
    },
    mutateRoles (state, data) {
      state.$_session.$_roles = data
    },
    clearAuth (state) {
      state.$_session.$_token = null
      state.$_session.$_user = null
      state.$_session.$_profile = {}
      state.$_session.$_timeStamp = null
      state.$_session.$_roles = null
      state.$_session.$_accessRoles = []
      state.$_mapsApi = null
      state.autocomplete = {}
      state.activeAppointments = []
      state = {}
      localStorage.removeItem('vuex')
    },
    mutateAppIntegration (state, data) {
      state.$_session.$_appIntegration = data
    },
    mutateHealthPlaceIntegration (state, data) {
      state.$_session.$_healthPlaceIntegration = data
    },
    mutateProgress (state, data) {
      state.progress = data
    },
    mutateModalAlert (state, data) {
      if (data) {
        state.modalAlert = data
      } else {
        state.modalAlert = {
          title: null,
          body: null,
          active: false,
          size: 'sm',
          callback: null
        }
      }
    },
    mutateModalAlertSimple (state, data) {
      if (data) {
        state.modalAlertSimple = data
      } else {
        state.modalAlertSimple = {
          title: null,
          body: null,
          active: false,
          size: 'sm',
          callback: null
        }
      }
    },
    mutateModalConfirm (state, data) {
      if (data) {
        state.modalConfirm = data
      } else {
        state.modalConfirm = {
          title: null,
          body: null,
          footer: null,
          active: false,
          confirmText: null,
          beforeClose: null,
          size: 'sm',
          callback: null,
          cancelText: null
        }
      }
    },
    mutateSidebar (state, data) {
      state.showSidebar = data || !state.showSidebar
    },
    mutateAutocomplete (state, data) {
      state.autocomplete[data.type] = data.data
    },
    mutateActiveAppointments (state, data) {
      state.activeAppointments = data || []
    },
    mutateNewCertifierCredentialRequired (state, data) {
      state.newCertifierCredentialRequired = data
    },
    mutateRegistrationAppointmentInterval (state, data) {
      state.registration.appointmentInterval = data
    },
    mutateCancelToken (state, data) {
      state.cancelToken = data
    },
    mutateAccessRoles (state, data) {
      state.$_session.$_accessRoles = data
    },
    mutateCallStack (state, data) {
      state.callStack = data
    },
    mutateTour (state, data) {
      for (let field in data) {
        state.tour[field] = data[field]
      }
    },
    mutateNewTab (state, data) {
      state.newTab = data
    },
    mutateActionModal (state, data) {
      state.actionModal = data
    },
    mutateTelemedicineBroadcasting (state, data) {
      state.telemedicine.isBroadcasting = data
    },
    mutateTelemedicineActive (state, data) {
      state.telemedicine.isActive = data
    },
    mutateAppointmentStart (state, data) {
      state.appointmentStart = data
    },
    mutateIsSidebarToggled (state, data) {
      state.isSidebarToggled = data
    },
    mutateViewport (state, data) {
      state.viewport = data
    },

    mutatePlan (state, data = {}) {
      for (const key in data) {
        if (key in state.plan) {
          state.plan[key] = data[key]
        }
      }
    },

    mutatePlanShowModal (state, { showModal, isReminderModal }) {
      state.plan.showModal = showModal
      state.plan.isReminderModal = isReminderModal
    },

    mutatePlanRemainingDays (state, days) {
      state.plan.remainingDays = days
    },

    mutatePlanIsUpgrade (state, data) {
      state.plan.isUpgrade = data
    }
  },
  actions: {
    setAppIntegration ({ commit }, data) {
      commit('mutateAppIntegration', data)
    },
    setHealthPlaceIntegration ({ commit }, data) {
      commit('mutateHealthPlaceIntegration', data)
    },
    setUserProfile ({commit}, data) {
      commit('mutateUserProfile', data)
    },
    setUserOffices ({ commit }, data) {
      commit('mutateUserOffices', data)
    },
    setCurrentOffice ({ commit }, data) {
      commit('mutateCurrentOffice', data)
    },
    unsetCurrentOffice ({ commit }) {
      commit('removeCurrentOffice')
    },
    updateUserProfile ({commit}, data) {
      commit('mutateUserProfileField', data)
    },
    setRoles ({commit}, data) {
      commit('mutateRoles', data)
    },
    setAuth ({commit}, data) {
      commit('mutateAuth', data)
    },
    clearAuth ({commit}) {
      commit('clearAuth')
    },
    toggleProgress ({commit}, data) {
      commit('mutateProgress', data)
    },
    setModalAlert ({commit}, data) {
      commit('mutateModalAlert', data)
    },
    setModalAlertSimple ({commit}, data) {
      commit('mutateModalAlertSimple', data)
    },
    setModalConfirm ({commit}, data) {
      commit('mutateModalConfirm', data)
    },
    toggleSidebar ({commit}, data) {
      commit('mutateSidebar', data)
    },
    setAutocomplete ({commit}, data) {
      commit('mutateAutocomplete', data)
    },
    setActiveAppointments ({commit}, data) {
      commit('mutateActiveAppointments', data)
    },
    setNewCertifierCredentialRequired ({commit}, data) {
      commit('mutateNewCertifierCredentialRequired', data)
    },
    setRegistrationAppointmentInterval ({commit}, data) {
      commit('mutateRegistrationAppointmentInterval', data)
    },
    setCancelToken ({commit}, data) {
      commit('mutateCancelToken', data)
    },
    setAccessRoles ({commit}, data) {
      commit('mutateAccessRoles', data)
    },
    setCallStack ({commit}, data) {
      commit('mutateCallStack', data)
    },
    updateTour ({commit}, data) {
      commit('mutateTour', data)
    },
    setNewTab ({commit}, data) {
      commit('mutateNewTab', data)
    },
    setActionModal ({commit}, data) {
      commit('mutateActionModal', data)
    },
    setTelemedicineBroadcasting ({ commit }, data) {
      commit('mutateTelemedicineBroadcasting', data)
    },
    setTelemedicineActive ({ commit }, data) {
      commit('mutateTelemedicineActive', data)
    },
    setAppointmentStart ({ commit }, data) {
      commit('mutateAppointmentStart', data)
    },
    setIsSidebarToggled ({ commit }, data) {
      commit('mutateIsSidebarToggled', data)
    },

    setPlan ({ commit }, data) {
      commit('mutatePlan', data)
    },

    togglePlanModal ({ commit }, { showModal = true, isReminderModal = false } = {}) {
      commit('mutatePlanShowModal', { showModal, isReminderModal })
    },

    setPlanRemainingDays ({ commit }, data) {
      commit('mutatePlanRemainingDays', data)
    },

    setPlanIsUpgrade ({ commit }, data) {
      commit('mutatePlanIsUpgrade', data)
    }
  },
  getters: {
    $_getMapsApi (state) {
      return state.$_mapsApi
    },
    $_checkAuth (state) {
      return state.$_session.$_token
    },
    getAuthHeader (state) {
      if (state.$_session) {
        return {
          'headers': {
            'Authorization': `Bearer ${state.$_session.$_token}`
          }
        }
      }
    },
    getAppIntegration (state) {
      return state.$_session.$_appIntegration
    },
    getHealthPlaceIntegration (state) {
      return state.$_session.$_healthPlaceIntegration
    },
    getProfile (state) {
      return state.$_session.$_profile
    },
    getToken (state) {
      return `Bearer ${state.$_session.$_token}`
    },
    getRefreshToken (state) {
      return state.$_session.$_refreshToken
    },
    getRoles (state) {
      return state.$_session.$_roles
    },
    getUser (state) {
      return state.$_session.$_user
    },
    getOffices (state) {
      return state.$_session.$_offices
    },
    getCurrentOffice (state) {
      return state.$_session.$_currentOffice
    },
    showProgress (state) {
      return state.progress
    },
    getAlert (state) {
      return state.modalAlert
    },
    getConfirm (state) {
      return state.modalConfirm
    },
    showSidebar (state) {
      return state.showSidebar
    },
    getAutocomplete: (state) => (type) => {
      if (!state.autocomplete[type]) {
        state.autocomplete[type] = []
      }
      return state.autocomplete[type]
    },
    getActiveAppointments (state) {
      return state.activeAppointments
    },
    getNewCertifierCredentialRequired (state) {
      return state.newCertifierCredentialRequired
    },
    getRegistrationAppointmentInterval (state) {
      return state.registration.appointmentInterval
    },
    getCancelToken (state) {
      return state.cancelToken
    },
    getAccessRoles (state) {
      return state.$_session.$_accessRoles
    },
    getCallStack (state) {
      return state.callStack
    },
    getTour (state) {
      return state.tour
    },
    getNewTab (state) {
      return state.newTab
    },
    getActionModal (state) {
      return state.actionModal
    },
    getTelemedicineBroadcasting (state) {
      return state.telemedicine.isBroadcasting
    },
    getTelemedicineActive (state) {
      return state.telemedicine.isActive
    },
    getAppointmentStart (state) {
      return state.appointmentStart
    },
    getIsSidebarToggled (state) {
      return state.isSidebarToggled
    },
    getViewport (state) {
      // based off tailwind.config breakpoints
      function setViewport () {
        if (window.innerWidth > 1024) {
          state.viewport = 'desktop'
        } else if (window.innerWidth <= 1024 && window.innerWidth > 740) {
          state.viewport = 'tablet'
        } else {
          state.viewport = 'mobile'
        }
        state.windowWidth = window.innerWidth
      }
      window.resizer = function () {
        return window.addEventListener('resize', () => {
          setViewport()
        })
      }
      setViewport()
      if (!window.resizer()) {
        window.resizer()
      }
      return {
        name: state.viewport,
        width: state.windowWidth
      }
    },

    getPlan (state) {
      return state.plan
    },

    getPlanShowModal (state) {
      return state.plan.showModal
    }
  }
})

export default store
